import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, ArrowUpRight, ArrowDownRight, ChevronDown, Eye,
  DollarSign, ShoppingCart, TrendingUp, Percent, Target, 
  BarChart3, Filter, X, CalendarDays
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area,  Cell, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];

// KPI Card Component
const KPICard = ({ title, value, icon: Icon, trend, trendValue, color = "blue", subtitle }) => {
  const colorClasses = {
    blue: "from-blue-500/10 to-blue-600/5 border-blue-500/20 text-blue-400",
    green: "from-green-500/10 to-green-600/5 border-green-500/20 text-green-400",
    purple: "from-purple-500/10 to-purple-600/5 border-purple-500/20 text-purple-400",
    orange: "from-orange-500/10 to-orange-600/5 border-orange-500/20 text-orange-400",
    teal: "from-teal-500/10 to-teal-600/5 border-teal-500/20 text-teal-400",
    pink: "from-pink-500/10 to-pink-600/5 border-pink-500/20 text-pink-400"
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} rounded-2xl p-6 border shadow-lg hover:shadow-xl transition-all hover:scale-102`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl bg-${color}-500/10`}>
          <Icon className="w-6 h-6" />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 ${trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>
            {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            <span className="text-sm font-medium">{trendValue}</span>
          </div>
        )}
      </div>
      <p className="text-sm text-gray-400 mb-2">{title}</p>
      <p className="text-3xl font-bold text-white mb-1">{value}</p>
      {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
    </div>
  );
};

// Custom Filter Modal
const FilterModal = ({ isOpen, onClose, onApply, currentDays }) => {
  const [filterType, setFilterType] = useState('preset');
  const [presetDays, setPresetDays] = useState(currentDays);
  const [customDays, setCustomDays] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  if (!isOpen) return null;

  const handleApply = () => {
    if (filterType === 'preset') {
      onApply({ type: 'days', value: presetDays });
    } else if (filterType === 'custom') {
      onApply({ type: 'days', value: parseInt(customDays) });
    } else if (filterType === 'range') {
      onApply({ type: 'range', startDate, endDate });
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 max-w-md w-full border border-gray-700 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white">تخصيص الفترة الزمنية</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Filter Type Tabs */}
          <div className="flex gap-2 bg-gray-800/50 p-1 rounded-xl">
            <button
              onClick={() => setFilterType('preset')}
              className={`flex-1 py-2 px-4 rounded-lg transition-all ${
                filterType === 'preset' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              محدد مسبقاً
            </button>
            <button
              onClick={() => setFilterType('custom')}
              className={`flex-1 py-2 px-4 rounded-lg transition-all ${
                filterType === 'custom' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              مخصص
            </button>
            
          </div>

          {/* Preset Days */}
          {filterType === 'preset' && (
            <div className="space-y-3">
              <label className="text-gray-300 text-sm">اختر الفترة:</label>
              <div className="grid grid-cols-3 gap-3">
                {[7, 14, 30, 60, 90, 180, 365].map(days => (
                  <button
                    key={days}
                    onClick={() => setPresetDays(days)}
                    className={`py-3 px-4 rounded-xl transition-all ${
                      presetDays === days
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50'
                    }`}
                  >
                    {days} يوم
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Custom Days */}
          {filterType === 'custom' && (
            <div className="space-y-3">
              <label className="text-gray-300 text-sm">أدخل عدد الأيام:</label>
              <input
                type="number"
                value={customDays}
                onChange={(e) => setCustomDays(e.target.value)}
                placeholder="مثال: 45"
                className="w-full bg-gray-800/50 text-white rounded-xl px-4 py-3 border border-gray-600/50 focus:border-blue-500/70 focus:outline-none"
              />
            </div>
          )}

          

          {/* Apply Button */}
          <button
            onClick={handleApply}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-600 transition-all"
          >
            تطبيق
          </button>
        </div>
      </div>
    </div>
  );
};

// Day Card Component
const DayCard = ({ day, onClick }) => (
  <div 
    className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 hover:scale-102 transition-all cursor-pointer shadow-lg hover:shadow-xl border border-gray-800 hover:border-gray-700 group"
    onClick={() => onClick(day)}
  >
    <div className="flex items-center justify-between mb-6">
      <div className="bg-blue-500/10 p-2 rounded-xl group-hover:bg-blue-500/20 transition-colors">
        <Calendar className="w-5 h-5 text-blue-400" />
      </div>
      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
        <Eye className="w-5 h-5 text-gray-400" />
      </div>
    </div>
    
    <h3 className="text-lg font-semibold text-gray-300 mb-4 group-hover:text-white transition-colors">
      {new Date(day.date).toLocaleDateString('ar-EG')}
    </h3>
    
    <div className="space-y-3">
      <div className="bg-gray-800/50 p-3 rounded-xl group-hover:bg-gray-700/50 transition-colors">
        <p className="text-xs text-gray-400 mb-1">إجمالي المبيعات</p>
        <p className="text-xl font-bold text-white">${day.total_price.toFixed(2)}</p>
      </div>
      
      <div className="bg-gray-800/50 p-3 rounded-xl group-hover:bg-gray-700/50 transition-colors">
        <p className="text-xs text-gray-400 mb-1">صافي الربح</p>
        <p className="text-xl font-bold text-green-400">${day.total_profit.toFixed(2)}</p>
      </div>
      
      <div className="bg-gray-800/50 p-3 rounded-xl group-hover:bg-gray-700/50 transition-colors">
        <p className="text-xs text-gray-400 mb-1">عدد الطلبات</p>
        <div className="flex items-center justify-between">
          <p className="text-xl font-bold text-white">{day.orders_count}</p>
          <ArrowUpRight className="text-blue-400 w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
        </div>
      </div>

      
    </div>
  </div>
);

// Main Dashboard Component
const DaysDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [daysCount, setDaysCount] = useState(7);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [kpis, setKpis] = useState({
    totalRevenue: 0,
    totalProfit: 0,
    totalOrders: 0,
    averageOrderValue: 0,
    dailyAverage: 0,
    profitMargin: 0,
    averageDailyProfit: 0
  });

  useEffect(() => {
    fetchData();
  }, [daysCount]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`https://dailycard.co/api/dashboard/profit-margin-summary/?days=${daysCount}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      const result = await response.json();
      const reversedData = result.reverse();
      setData(reversedData);
      calculateKPIs(reversedData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateKPIs = (dataArray) => {
    const totalRevenue = dataArray.reduce((sum, day) => sum + day.total_price, 0);
    const totalProfit = dataArray.reduce((sum, day) => sum + day.total_profit, 0);
    const totalOrders = dataArray.reduce((sum, day) => sum + day.orders_count, 0);
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    const dailyAverage = dataArray.length > 0 ? totalRevenue / dataArray.length : 0;
    const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
    const averageDailyProfit = dataArray.length > 0 ? totalProfit / dataArray.length : 0;
    
    setKpis({
      totalRevenue,
      totalProfit,
      totalOrders,
      averageOrderValue,
      dailyAverage,
      profitMargin,
      averageDailyProfit
    });
  };

  const getAgentGrowthData = () => {
    const agentsByDay = {};
    
    data.forEach(day => {
      const formattedDate = new Date(day.date).toLocaleDateString('ar-EG', { month: 'short', day: 'numeric' });
      
      day.agent_statistics.forEach(agent => {
        if (!agentsByDay[agent.agent_name]) {
          agentsByDay[agent.agent_name] = [];
        }
        
        agentsByDay[agent.agent_name].push({
          date: formattedDate,
          revenue: agent.total_base_price,
          orders: agent.orders_count
        });
      });
    });
    
    return agentsByDay;
  };

  const getAgentPerformanceOverTime = () => {
    const agentsByDay = {};
    
    data.forEach(day => {
      const formattedDate = new Date(day.date).toLocaleDateString('ar-EG', { month: 'short', day: 'numeric' });
      agentsByDay[formattedDate] = { date: formattedDate };
      
      day.agent_statistics.forEach(agent => {
        agentsByDay[formattedDate][agent.agent_name] = agent.total_base_price;
      });
    });
    
    return Object.values(agentsByDay);
  };


  const handleFilterApply = (filter) => {
    if (filter.type === 'days') {
      setDaysCount(filter.value);
    }
    // يمكن إضافة معالجة لـ date range لاحقاً
  };

  const handleDayClick = (day) => {
    navigate(`/profit-margin/day/${day.date}`, { 
      state: { dayData: day } 
    });
  };

  // تحضير بيانات الرسوم البيانية
  const chartData = data.map(day => ({
    date: new Date(day.date).toLocaleDateString('ar-EG', { month: 'short', day: 'numeric' }),
    revenue: day.total_price,
    profit: day.total_profit,
    orders: day.orders_count,
    margin: ((day.total_profit / day.total_price) * 100).toFixed(2)
  }));

  // بيانات توزيع الوكلاء
  const getTopAgents = () => {
    const agentTotals = {};
    data.forEach(day => {
      day.agent_statistics.forEach(agent => {
        if (!agentTotals[agent.agent_name]) {
          agentTotals[agent.agent_name] = {
            name: agent.agent_name,
            value: 0,
            orders: 0
          };
        }
        agentTotals[agent.agent_name].value += agent.total_base_price;
        agentTotals[agent.agent_name].orders += agent.orders_count;
      });
    });
    return Object.values(agentTotals)
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
          <p className="text-white text-lg">جاري تحميل البيانات...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" dir="rtl">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 container mx-auto p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-2">
              تحليل الأرباح المتقدم
            </h1>
            <p className="text-gray-400 text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              عرض شامل للمبيعات والأرباح اليومية
            </p>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => setIsFilterOpen(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-gray-800/80 to-gray-700/80 backdrop-blur-sm text-white rounded-xl px-6 py-3 border border-gray-600/50 hover:border-blue-500/70 transition-all shadow-lg"
            >
              <Filter className="w-5 h-5" />
              <span>تخصيص الفترة</span>
            </button>
            
           
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <KPICard
            title="إجمالي الإيرادات"
            value={`$${kpis.totalRevenue.toLocaleString(undefined, {maximumFractionDigits: 2})}`}
            icon={DollarSign}
            color="blue"
            subtitle={`${daysCount} يوم`}
          />
          <KPICard
            title="صافي الربح"
            value={`$${kpis.totalProfit.toLocaleString(undefined, {maximumFractionDigits: 2})}`}
            icon={TrendingUp}
            color="green"
            subtitle="الأرباح الصافية"
          />
          <KPICard
            title="إجمالي الطلبات"
            value={kpis.totalOrders.toLocaleString()}
            icon={ShoppingCart}
            color="purple"
            subtitle="عدد الطلبات"
          />
          
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue vs Profit Chart */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-800 shadow-lg">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-400" />
              الإيرادات مقابل الأرباح
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend />
                <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fillOpacity={1} fill="url(#colorRevenue)" name="الإيرادات" />
                <Area type="monotone" dataKey="profit" stroke="#10b981" fillOpacity={1} fill="url(#colorProfit)" name="الأرباح" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Orders Trend Chart */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-800 shadow-lg">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-purple-400" />
              تطور عدد الطلبات
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend />
                <Bar dataKey="orders" fill="#8b5cf6" name="عدد الطلبات" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Agents Analysis Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Agents by Sales */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-800 shadow-lg">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-orange-400" />
              أعلى الوكلاء من حيث المبيعات
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={getTopAgents()} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis type="number" stroke="#9ca3af" />
                <YAxis dataKey="name" type="category" stroke="#9ca3af" width={100} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value) => `$${value.toFixed(2)}`}
                />
                <Bar dataKey="value" fill="#f59e0b" radius={[0, 8, 8, 0]} name="المبيعات">
                  {getTopAgents().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Top Agents by Orders */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-800 shadow-lg">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-teal-400" />
              أعلى الوكلاء من حيث عدد الطلبات
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={getTopAgents()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#9ca3af" angle={-45} textAnchor="end" height={80} />
                <YAxis stroke="#9ca3af" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Bar dataKey="orders" fill="#14b8a6" radius={[8, 8, 0, 0]} name="عدد الطلبات">
                  {getTopAgents().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Agents Comparison Line Chart */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-800 shadow-lg mb-8">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-400" />
            مقارنة أداء الوكلاء عبر الأيام (المبيعات)
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={getAgentPerformanceOverTime()}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
                formatter={(value) => `$${value.toFixed(2)}`}
              />
              <Legend />
              {getTopAgents().slice(0, 5).map((agent, index) => (
                <Line 
                  key={agent.name}
                  type="monotone" 
                  dataKey={agent.name} 
                  stroke={COLORS[index % COLORS.length]} 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Individual Agent Growth Charts */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-green-400" />
            تحليل نمو الوكلاء 
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {getTopAgents().slice(0, 6).map((agent, index) => {
              const agentData = data.map(day => {
                const agentStats = day.agent_statistics.find(a => a.agent_name === agent.name);
                return {
                  date: new Date(day.date).toLocaleDateString('ar-EG', { month: 'short', day: 'numeric' }),
                  revenue: agentStats?.total_base_price || 0,
                  orders: agentStats?.orders_count || 0
                };
              });

              return (
                <div 
                  key={agent.name}
                  className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-800 shadow-lg"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      {agent.name}
                    </h3>
                    <div className="text-sm text-gray-400">
                      إجمالي: ${agent.value.toFixed(2)}
                    </div>
                  </div>

                  <ResponsiveContainer width="100%" height={250}>
                    <ComposedChart data={agentData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#9ca3af" 
                        tick={{ fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis 
                        yAxisId="left"
                        stroke="#9ca3af" 
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis 
                        yAxisId="right"
                        orientation="right"
                        stroke="#9ca3af" 
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1f2937', 
                          border: '1px solid #374151', 
                          borderRadius: '8px' 
                        }}
                        labelStyle={{ color: '#fff' }}
                      />
                      <Legend />
                      <Bar 
                        yAxisId="left"
                        dataKey="revenue" 
                        fill={COLORS[index % COLORS.length]} 
                        radius={[8, 8, 0, 0]}
                        name="المبيعات ($)"
                        opacity={0.8}
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="orders" 
                        stroke="#ef4444" 
                        strokeWidth={3}
                        dot={{ fill: '#ef4444', r: 4 }}
                        name="عدد الطلبات"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>

                  {/* Growth Stats */}
                  {/* <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="bg-gray-800/50 p-3 rounded-xl">
                      <p className="text-xs text-gray-400 mb-1">متوسط المبيعات اليومية</p>
                      <p className="text-lg font-bold text-white">
                        ${(agent.value / data.length).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-gray-800/50 p-3 rounded-xl">
                      <p className="text-xs text-gray-400 mb-1">متوسط الطلبات اليومية</p>
                      <p className="text-lg font-bold text-white">
                        {(agent.orders / data.length).toFixed(0)}
                      </p>
                    </div>
                  </div> */}
                </div>
              );
            })}
          </div>
        </div>

        

        {/* Days Grid */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-blue-400" />
            تفاصيل الأيام
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
            {data.map((day) => (
              <DayCard 
                key={day.date} 
                day={day} 
                onClick={handleDayClick}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      <FilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleFilterApply}
        currentDays={daysCount}
      />
    </div>
  );
};

export default DaysDashboard;